import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LayoutService } from './layout.service';

@Injectable({
  providedIn: 'root'
})
export class VisualAnalysisService {
  private openSubject = new BehaviorSubject<boolean>(false);
  private customColorMapSubject = new BehaviorSubject<Map<number, string>>(new Map<number, string>());
  
  public open$ = this.openSubject.asObservable();
  public customColorMap$ = this.customColorMapSubject.asObservable();

  get isOpen(): boolean {
    return this.openSubject.value;
  }

  constructor(private layoutService: LayoutService){

  }

  toggle() {
    this.openSubject.next(!this.openSubject.value);
  }

  setOpen(open: boolean) {
    if (this.openSubject.value !== open) {
      this.openSubject.next(open);
    }
  }

  setProductColor(productId: number, color: string) {
    const currentMap = this.customColorMapSubject.value;
    const newMap = new Map(currentMap);
    newMap.set(productId, color);
    this.customColorMapSubject.next(newMap);
  }

  /** Bulk variant of setProductColor. Folds every (productId, color) update
   *  into a single new Map and emits customColorMap$ once. updateAnalysis
   *  used to call setProductColor in a per-product loop — each call emitted
   *  customColorMap$, which fanned out to every image component AND triggered
   *  refreshLegendMap (which emits productLegendMap$ to every product
   *  component). For 1000+ products that's the dominant cost of redo / drop. */
  setProductColors(updates: Iterable<readonly [number, string]>) {
    const newMap = new Map(this.customColorMapSubject.value);
    for (const [productId, color] of updates) {
      newMap.set(productId, color);
    }
    this.customColorMapSubject.next(newMap);
  }

  clearProductColor(productId: number) {
    const currentMap = this.customColorMapSubject.value;
    const newMap = new Map(currentMap);
    newMap.delete(productId);
    this.customColorMapSubject.next(newMap);
  }

  /** Bulk variant of clearProductColor — one emission for the whole group. */
  clearProductColors(productIds: Iterable<number>) {
    const newMap = new Map(this.customColorMapSubject.value);
    for (const productId of productIds) {
      newMap.delete(productId);
    }
    this.customColorMapSubject.next(newMap);
  }

  clearAllColors() {
    this.customColorMapSubject.next(new Map<number, string>());
  }

  getCustomColors() {
    return this.customColorMapSubject.value;
  }
}